import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress, Alert, Grid, Card, CardActionArea, CardContent, Typography, TextField, Paper
} from '@mui/material';
import {
  IconUsers, IconBackpack, IconBeach, IconHeart, IconBuildingCottage, IconCalendar, IconUserCircle,
  IconMountain, // <-- FIX: Changed from IconMountains to IconMountain
  IconTrees, IconBuildingFortress, IconConfetti
} from '@tabler/icons-react';

import WizardLayout from '../../components/planner/WizardLayout';
import FinalDetailsForm from '../../components/planner/FinalDetailsForm';
import { fetchData } from '../../api/publicService';
import { submitInquiry } from '../../api/inquiryService';

const STEP_TITLES = ['Travel Style', 'Destinations', 'Interests', 'Accommodation', 'Dates', 'Your Details'];

// --- Helper Components for Selections ---
const SelectionCard = ({ icon, label, onClick, isSelected }) => (
    // This component is fine as is
    <Grid item size={{ xs: 6, sm: 4, md: 3 }}> 
      <Card variant={isSelected ? "outlined" : "elevation"} sx={{ borderColor: isSelected ? 'primary.main' : 'transparent', borderWidth: 2 }}>
        <CardActionArea onClick={onClick} sx={{ p: 3, textAlign: 'center' }}>
          <Box color={isSelected ? 'primary.main' : 'text.secondary'} mb={1}>{icon}</Box>
          <Typography fontWeight={isSelected ? 'bold' : 'normal'}>{label}</Typography>
        </CardActionArea>
      </Card>
    </Grid>
);

const MultiSelectionCard = ({ label, image, onClick, isSelected }) => (
  <Card sx={{ position: 'relative', borderWidth: 2, borderColor: isSelected ? 'primary.main' : 'transparent', borderStyle: 'solid', height: '100%' }}>
    <CardActionArea onClick={onClick} sx={{ height: '100%'}}>
      <Box component="img" src={image} alt={label} sx={{ height: 180, width: '100%', objectFit: 'cover' }} />
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: 'rgba(0, 0, 0, 0.6)', color: 'white', p: 1 }}>
        <Typography variant="h6" align="center">{label}</Typography>
      </Box>
    </CardActionArea>
  </Card>
);

const initialFormData = {
  travelStyle: 'Couple',
  focus: 'Relaxation',
  selectedDestinations: [],
  interests: [],
  accommodationStyle: 'Boutique Hotels',
  duration: '',
  tripDetails: { travelers: { adults: 1, children: 0 }, dates: '', budget: '' },
  contactDetails: { name: '', email: '', phone: '' },
};

const BuildTripWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadDestinations = async () => {
      try {
        const data = await fetchData('/destinations', i18n.language);
        if (Array.isArray(data)) {
            setDestinations(data);
            // DEBUGGING TIP: Uncomment this line to see the exact data you received
            console.log("Fetched Destinations:", data); 
        } else {
            setError('Received invalid data for destinations.');
        }
      } catch (err) {
        setError('Could not load destinations. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadDestinations();
  }, [i18n.language]);

  const handleNext = () => currentStep === 6 ? handleSubmit() : setCurrentStep(p => p + 1);
  const handleBack = () => setCurrentStep(p => p - 1);
  
  const handleSingleSelect = (field, value) => setFormData(p => ({ ...p, [field]: value }));
  const handleMultiSelect = (field, value) => {
    setFormData(p => ({
      ...p,
      [field]: p[field].includes(value)
        ? p[field].filter(item => item !== value)
        : [...p[field], value],
    }));
  };
  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleFinalDetailsUpdate = data => setFormData(data);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // --- THIS IS THE FIX ---
      const payload = {
        inquiryType: 'Custom Build',
        contactDetails: formData.contactDetails,
        // The duration from the top-level formData needs to be moved inside tripDetails for the payload
        tripDetails: {
          travelers: formData.tripDetails.travelers,
          dates: formData.tripDetails.dates,
          budget: formData.tripDetails.budget,
          duration: formData.duration, // <-- Correctly placed here
        },
        customBuildDetails: {
          travelStyle: formData.travelStyle,
          focus: formData.focus,
          selectedDestinations: formData.selectedDestinations,
          interests: formData.interests,
          accommodationStyle: formData.accommodationStyle,
        },
      };
      await submitInquiry(payload);
      navigate('/plan-your-trip/thank-you');
    } catch (err) {
      setError('Submission failed. Please check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>Who are you traveling with?</Typography>
            <Grid container spacing={2}>
              <SelectionCard icon={<IconUserCircle size={48} />} label="Solo" onClick={() => handleSingleSelect('travelStyle', 'Solo')} isSelected={formData.travelStyle === 'Solo'} />
              <SelectionCard icon={<IconHeart size={48} />} label="Couple" onClick={() => handleSingleSelect('travelStyle', 'Couple')} isSelected={formData.travelStyle === 'Couple'} />
              <SelectionCard icon={<IconUsers size={48} />} label="Family" onClick={() => handleSingleSelect('travelStyle', 'Family')} isSelected={formData.travelStyle === 'Family'} />
              <SelectionCard icon={<IconConfetti size={48} />} label="Group" onClick={() => handleSingleSelect('travelStyle', 'Group')} isSelected={formData.travelStyle === 'Group'} />
            </Grid>
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>What is the primary focus of your trip?</Typography>
            <Grid container spacing={2}>
                <SelectionCard icon={<IconBeach size={48} />} label="Relaxation" onClick={() => handleSingleSelect('focus', 'Relaxation')} isSelected={formData.focus === 'Relaxation'} />
                <SelectionCard icon={<IconBackpack size={48} />} label="Adventure" onClick={() => handleSingleSelect('focus', 'Adventure')} isSelected={formData.focus === 'Adventure'} />
                <SelectionCard icon={<IconBuildingFortress size={48} />} label="Culture" onClick={() => handleSingleSelect('focus', 'Culture')} isSelected={formData.focus === 'Culture'} />
                <SelectionCard icon={<IconTrees size={48} />} label="Nature" onClick={() => handleSingleSelect('focus', 'Nature')} isSelected={formData.focus === 'Nature'} />
            </Grid>
          </Paper>
        );
      case 2:
        if (loading) return <CircularProgress />;
        return (
            <Grid container spacing={3}>
              {destinations.map(dest => (
                // --- FIX 2: Pass the `shortDescription` to the new `description` prop ---
                <MultiSelectionCard 
                    key={dest._id} 
                    label={dest.name} 
                    description={dest.shortDescription} // <-- Pass the description here
                    image={dest.cardImage} 
                    onClick={() => handleMultiSelect('selectedDestinations', dest._id)} 
                    isSelected={formData.selectedDestinations.includes(dest._id)} 
                />
              ))}
            </Grid>
        );
      case 3:
        const interests = [{name: 'Wildlife Safari', icon: <IconTrees size={48}/>}, {name: 'Hiking & Trekking', icon: <IconMountain size={48} />}, {name: 'Beach Time', icon: <IconBeach size={48}/>}, {name: 'Historical Sites', icon: <IconBuildingFortress size={48}/>}];
        return (
          <Paper sx={{ p: 4 }}>
            <Grid container spacing={2}>
              {interests.map(interest => (
                <SelectionCard key={interest.name} icon={interest.icon} label={interest.name} onClick={() => handleMultiSelect('interests', interest.name)} isSelected={formData.interests.includes(interest.name)} />
              ))}
            </Grid>
          </Paper>
        );
      case 4:
        const accommodations = [{name: 'Boutique Hotels'}, {name: 'Luxury Resorts'}, {name: 'Villas & Bungalows'}, {name: 'Eco-lodges'}];
        return (
          <Paper sx={{ p: 4 }}>
            <Grid container spacing={2}>
              {accommodations.map(acc => (
                <SelectionCard key={acc.name} icon={<IconBuildingCottage size={48}/>} label={acc.name} onClick={() => handleSingleSelect('accommodationStyle', acc.name)} isSelected={formData.accommodationStyle === acc.name} />
              ))}
            </Grid>
          </Paper>
        );
      case 5:
        return (
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>Duration & Dates</Typography>
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Desired trip duration" name="duration" value={formData.duration} onChange={handleChange} helperText="e.g., 10 days, 2 weeks"/>
                    </Grid>
                </Grid>
            </Paper>
        );
      case 6:
        return <FinalDetailsForm data={formData} onUpdate={handleFinalDetailsUpdate} />;
      default: return null;
    }
  };

  return (
    <WizardLayout
      step={currentStep} totalSteps={6} title="Build Your Custom Trip" stepTitles={STEP_TITLES}
      onNext={handleNext} onBack={handleBack}
      isFirstStep={currentStep === 1} isLastStep={currentStep === 6}
      isSubmitting={submitting} submitText="Get My Custom Quote"
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {renderStepContent()}
    </WizardLayout>
  );
};

export default BuildTripWizard;
